import React from 'react';
import { Component } from 'react';
import _ from 'lodash';
import { omit, assign } from 'lodash';
import { stringIf, buildClassName } from '../util';
import classNames from 'classnames';
import { Glyphicon } from './icons';

/**
 * Miscellaneous UI components
 * @module
 */

/** Factory for creating components that consist of a div with a class name. */
export function createDivComponent(className: string) {
  return createSimpleComponent<HTMLDivElement>('div', className);
}

/** Factory for components that are defined only by an element with a class name. */
export function createSimpleComponent<T>(
  elementType: string,
  className: string,
) {
  return (props: React.HTMLProps<T>) => {
    return React.createElement(
      elementType,
      assign({}, props, {
        className:
          className + stringIf(!!props.className, ' ' + props.className),
      }),
    );
  };
}

interface TextInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  leftAddon?: string;
}

export function TextInput(props: TextInputProps) {
  const className = classNames('form-control', props.className);
  let inner;
  let inputProps = omit(props, 'leftAddon');
  if (props.leftAddon) {
    inner = (
      <div className="input-group">
        <span className="input-group-addon">{props.leftAddon}</span>
        <input type="text" {...inputProps} className={className} />
      </div>
    );
  } else {
    inner = <input type="text" {...inputProps} className={className} />;
  }
  return <div className="form-group">{inner}</div>;
  /*
    return <div className="form-group">
        <input type="text" {...inputProps} className={className} />
    </div> */
}

/*
export function ToolbarTextInput(props: React.HTMLAttributes) {
    return <div className="toolbar-text-input">
        <TextInput {...props} />
    </div>
}
*/

export const ToolbarLabel = createDivComponent('toolbar-label');
export const ToolbarRightAlign = createDivComponent('toolbar-right-align');

export const ToolbarTextBox = createDivComponent('toolbar-text-box');
//export const Toolbar = createDivComponent("toolbar")

interface ToolbarProps extends React.HTMLAttributes<HTMLDivElement> {
  fixed?: boolean;
}
/** Toolbar thing used in the script and filesystem pages. */
export function Toolbar(props: ToolbarProps) {
  const className = classNames(
    props.fixed ? 'toolbar-fixed' : 'toolbar',
    props.className,
  );
  return (
    <div {...omit(props, 'fixed')} className={className}>
      {props.children}
    </div>
  );
}

interface ToolbarButtonProps extends React.HTMLAttributes<HTMLDivElement> {
  btnStyle?: 'primary' | 'bare';
  mergeRight?: boolean;
  icon?: string;
}
export function ToolbarButton(props: ToolbarButtonProps) {
  const { btnStyle, mergeRight } = props;
  const className = classNames(
    buildClassName(btnStyle, `toolbar-button-${btnStyle}`, 'toolbar-button'),
    { 'merge-right': mergeRight },
    props.className,
  );
  const icon = !!props.icon ? glyphicon(props.icon) : null;
  const space = !!props.icon ? ' ' : null;
  return (
    <div {...omit(props, 'btnStyle', 'mergeRight')} className={className}>
      {icon}
      {space}
      {props.children}
    </div>
  );
}

/** Components for building panels. */
export const panel = {
  Panel: createDivComponent('kabuto-live-panel'),
  Header: createDivComponent('header'),
  HeaderCenter: createDivComponent('header-center'),
  Fixed: createDivComponent('fixed'),
  FixedCenter: createDivComponent('fixed-center'),
  Flex: createDivComponent('flexible'),
  FlexFixed: createDivComponent('flex-fixed'),
};

export const Panel = createDivComponent('kabuto-live-panel');
export const FlexRow = createDivComponent('flex-row');
export const FlexCol = createDivComponent('flex-col');

/** Unfinished meter thing. SVG is hard! */
export function Meter() {
  return (
    <svg viewBox="0 0 100 75">
      <path
        d="M25 40 C 35 20 65 20 75 40"
        style={{ strokeWidth: 20 }}
        stroke="black"
        fill="transparent"
      />
    </svg>
  );
}

interface ToggleSwitchProps {
  onChange?: (newValue: boolean) => any;
  label?: string;
  defaultState?: boolean;
  key?: any;
}

interface ToggleSwitchState {
  on?: boolean;
}

/** Toggle switch component. Used in the settings panel. */
export class ToggleSwitch extends Component<
  ToggleSwitchProps,
  ToggleSwitchState
> {
  constructor(props, context) {
    super(props, context);
    this.state = { on: this.props.defaultState };
  }
  innerSwitch() {
    let { key } = this.props;
    let { on } = this.state;
    return (
      <div
        key={key}
        className={classNames(['toggle-switch', { on }])}
        onClick={() => {
          if (this.props.onChange) {
            this.props.onChange(!this.state.on);
          }
          this.setState({ on: !this.state.on });
        }}
      >
        <div className="inner">
          <div>
            <Glyphicon
              glyph={on ? 'ok' : ('remove' as any) /* TODO Fix it */}
            />
          </div>
        </div>
      </div>
    );
  }
  render() {
    let { label, key } = this.props;
    if (label) {
      return (
        <div>
          <div key={key} className="toggle-label">
            {label}
          </div>
          {this.innerSwitch()}
        </div>
      );
    }
    return this.innerSwitch();
  }
}

declare let require: (string) => any;
require('justgage');
declare let JustGage: any;

let gaugeId = 0;
/** Generate a freakin ID for the gauge. */
function getGaugeId() {
  gaugeId++;
  return 'gauge' + gaugeId;
}

interface GaugeProps {
  title?: string;
  label: string;
  value: string | number;
  min: string | number;
  max: string | number;
  width?: number;
  height?: number;
}

/** Very nasty wrapper around the justgage gauge. */
export class Gauge extends Component<GaugeProps, {}> {
  gauge: any;
  id: string;
  componentDidMount() {
    this.gauge = new JustGage({
      id: this.id,
      value: Number(this.props.value),
      min: Number(this.props.min),
      max: Number(this.props.max),
      label: this.props.label || '',
      title: this.props.title || '',
    });
  }
  componentWillReceiveProps(nextProps: GaugeProps) {
    if (nextProps.value !== this.props.value) {
      this.gauge.refresh(nextProps.value);
    }
    if (nextProps.max !== this.props.max) {
      this.gauge.refresh(this.props.value, nextProps.max);
    }
  }
  constructor(props, context) {
    super(props, context);
    this.id = getGaugeId();
  }
  inner() {
    let { height, width } = this.props;
    if (width && height) {
      return <div id={this.id} className={width + 'x' + height + 'px gauge'} />;
    }
    return <div id={this.id} className="gauge" />;
  }
  render() {
    return (
      <div className="gauge-outer">
        {this.inner()}
        <span className="stat-item-head-text">{this.props.title}</span> <br />
        <span className="stat-item-body-text">
          {this.props.value}
          {this.props.label}
        </span>
      </div>
    );
  }
}

/** Component that horizontally and vertically centers its child. */
export function Center(
  props: React.HTMLAttributes<HTMLDivElement> & { noHeight?: boolean },
) {
  const { noHeight, ...otherProps } = props;
  return (
    <div
      {...otherProps}
      className={classNames(
        {
          'center-parent': !noHeight,
          'center-parent-no-height': noHeight,
        },
        props.className,
      )}
    >
      <div className="center-child">{props.children}</div>
    </div>
  );
}

interface TabPanelState {}

type TabPageProps = React.HTMLAttributes<HTMLDivElement> & { title?: string };
export function TabPage(props: TabPageProps) {
  let { title, ...rest } = props;
  return <div {...rest}>{props.children}</div>;
}

interface TabPanelProps extends React.HTMLAttributes<HTMLDivElement> {
  onAdd?: (terminalType: string) => any;
  onClose?: (index: number) => any;
  onChangeTab?: (index: number) => any;
  currentTab?: number;
  tabs?: string[];
  //children: ReactElement<TabPageProps> | ReactElement<TabPageProps>[]
}

export function glyphicon(
  glyph: string,
  passthroughProps: React.HTMLAttributes<HTMLSpanElement> = {},
) {
  return (
    <span
      {...passthroughProps}
      className={classNames(
        'glyphicon glyphicon-' + glyph,
        passthroughProps.className,
      )}
    />
  );
}

/** Tab panel component used in the terminal. */
export class TabPanel extends Component<TabPanelProps, TabPanelState> {
  constructor(props, context) {
    super(props, context);
  }
  closeButton(index: number) {
    if (this.props.onClose) {
      return (
        <span
          style={{ cursor: 'pointer' }}
          className={'close-icon glyphicon glyphicon-remove'}
          onClick={e => {
            e.stopPropagation();
            this.props.onClose(index);
          }}
        />
      );
    } else {
      return null;
    }
  }
  getTab(title: string, index: number) {
    let active = this.equalsCurrentTab(index);
    return (
      <div
        key={index}
        className={classNames('tab-bar-tab', { 'tab-bar-tab-active': active })}
        onClick={() => this.changeTab(index)}
      >
        {title} &nbsp; {this.closeButton(index)}
      </div>
    );
  }
  createTerminalTab() {
    if (this.props.tabs.length > 0) {
      return (
        <div className="tab-bar-tab-create">
          <div className="dropdown drop-width-140">
            <div className="dropbtn">Start Terminal</div>
            <div className="dropdown-content">
              <div
                className="drop-width-140"
                onClick={() => this.props.onAdd('cmd.exe')}
              >
                {'cmd.exe'}
              </div>
              <div
                className="drop-width-140"
                onClick={() => this.props.onAdd('powershell')}
              >
                {'powershell'}
              </div>
            </div>
          </div>
        </div>
      );
    } else {
      return null;
    }
  }
  equalsCurrentTab(i: number) {
    return i === this.props.currentTab;
  }
  changeTab(index: number) {
    this.props.onChangeTab && this.props.onChangeTab(index);
  }
  render() {
    let { tabs } = this.props;
    let renderedTabs: React.ReactNode[];
    renderedTabs = _.map(tabs, (tabTitle, i) => {
      return this.getTab(tabTitle, i);
    });
    return (
      <div className="tab-bar">
        {renderedTabs}
        {this.createTerminalTab()}
      </div>
    );
  }
}

interface FontAwesomeProps {
  fa: string;
  hidden?: boolean;
  size?: string;
}

export function FontAwesome({ fa, hidden, size }: FontAwesomeProps) {
  const className = classNames(`fa fa-${fa}`, { [`fa-${size}`]: !!size });
  return <i className={className} aria-hidden={hidden ? 'true' : 'false'} />;
}
